// Lays the system map out at its three levels and holds the level rules.
//
// All: a few big group cards (Competition types, Playing, ...) on a calm grid
// and nothing else. Group: that group's areas, one card each (illustration,
// name, how many parts), with the few main relationships between them as soft
// connectors. Area: that area's parts as a top-down
// tree that wraps: a parent's children flow into shelves (rows) and stack into
// columns so the tree fits the width of the screen at a READABLE size rather
// than shrinking its text. Siblings are ordered by the steps between them (and
// between what they hold), so the flow reads left to right, top to bottom, and
// the steps inside an area are drawn as arrowed connectors routed AROUND the
// other cards (orthogonal, like the holds rails), never behind one. Each level
// is its own coordinate space (the page swaps the DOM per level), so the camera
// maths here is per level: what fits, how far you may zoom either way, how far
// you may pan, and when zooming past the limit changes level. The chips beside
// a hovered part are placed by placeTray in free screen space, never over
// another part; a tray placed away from its card is reached along a corridor.
//
// Pure functions so the test can prove the rules and the page can recompute
// on every toggle and resize.

import {
  SYSTEM_MAP_GROUPS, SYSTEM_MAP_LINKS, SYSTEM_MAP_NODES, SYSTEM_MAP_SECTIONS, SYSTEM_MAP_STEPS, areaRelations, findSystemMapGroup,
  findSystemMapNode, findSystemMapSection, sectionTone, systemMapGroupOf, systemMapGroupSections, visibleSystemMapIds,
} from '~/utils/systemMap';

// Parts (area level)
export const NODE_W = 248;
/** Tall enough for a two-line name and a three-line description, so no description ends in "…". */
export const NODE_H = 104;
/** A child's indent from its parent; the rail that joins them runs in this gutter. */
export const GUTTER = 28;
/** Between columns in a shelf, between stacked parts in a column, under a parent, between shelves. */
export const COL_GAP = 36;
export const ROW_GAP = 22;
export const HEAD_GAP = 30;
export const SHELF_GAP = 30;
/** How far a routed connector keeps from any card it passes. */
export const ROUTE_MARGIN = 10;

// Group cards (all level) and area cards (group level): one card shape, an
// illustration, a name and a count, nothing else (Grant 2026-10-25: no
// subtitles at the overview; an area's line shows once you are inside it).
export const AREA_W = 480;
export const AREA_H = 320;
/** The phone's card: two across, a tile with its own (larger) type. */
export const AREA_H_COMPACT = 240;
export const AREA_GAP = 64;
export const AREA_GAP_COMPACT = 28;
/** Group cards per row on a desktop (four groups: two by two) and on a phone. */
export const ALL_COLUMNS = 2;
/** The most area cards in a row inside a group; a group with more takes balanced rows (five areas: three and two). */
export const GROUP_MAX_COLUMNS = 4;
export const OVERVIEW_COLUMNS_COMPACT = 2;
/**
 * A phone held upright shows the groups as one column of tall tiles that fill
 * the band between the pill and the bar (R53 L1); a group's areas stay two
 * across, their tiles grown to fill the band (up to AREA_H_COMPACT_MAX).
 */
export const ALL_COLUMNS_PORTRAIT = 1;
/** A phone on its side (wider than tall) lays the cards out like a desktop: the groups in one row, areas three across at most (R53 M4). */
export const ALL_COLUMNS_LANDSCAPE = 4;
export const GROUP_MAX_COLUMNS_LANDSCAPE = 3;
/** The most a phone tile grows in height to fill the band. */
export const AREA_H_COMPACT_MAX = 400;

// Type sizes in each layer's own units, bound into the CSS by the components,
// so the test can prove what they come to on screen at every entry view.
export const PART_NAME_PX = 15;
export const PART_LINE_PX = 12.5;
export const PART_LABEL_PX = 12;
export const AREA_NAME_PX = 32;
/** The count pill ("8 parts", "5 areas") is the card's smallest type: 11px+ on screen at every fit (R48 L5). */
export const AREA_COUNT_PX = 19;
/** The phone tile's type: sized for two tiles across a 390px screen. */
export const AREA_NAME_PX_COMPACT = 44;
export const AREA_COUNT_PX_COMPACT = 32;
/** The smallest a part name / a line may be on screen at an entry view. */
export const MIN_NAME_ON_SCREEN = 13;
export const MIN_LINE_ON_SCREEN = 11;
/** The most a card's type grows to stay readable on a short canvas. */
export const OVERVIEW_TYPE_MAX_BOOST = 1.6;
/** The longest a part's one-line description may be, so three lines always hold it. */
export const PART_LINE_MAX_CHARS = 66;
export const PART_NAME_MAX_CHARS = 36;
/** The longest an area's line may be: it reads on one line under the area's name in the bar. */
export const AREA_LINE_MAX_CHARS = 60;

/** The three levels: every group as a card, one group's areas as cards, one area's parts. */
export const LEVELS = Object.freeze({ALL: 'all', GROUP: 'group', AREA: 'area'});

/** Below this scale a part card's text stops being comfortable to read (15px name → 13.5px, 12.5px line → 11.25px). */
export const READABLE_SCALE = 0.9;
/**
 * The scale an area may open at when that makes it fit whole or puts the
 * screen's bottom edge in a gap between cards instead of through one: a line
 * is exactly MIN_LINE_ON_SCREEN there (11px), a name 13.2px (R48 M2).
 */
export const ENTRY_FLOOR_SCALE = MIN_LINE_ON_SCREEN / PART_LINE_PX;
/** The most an area's entry view grows past the readable scale to land the screen's bottom edge in a gap. */
export const ENTRY_CEILING_SCALE = 1;
/** The largest a part card gets: about twice its natural size. */
export const PART_MAX_SCALE = 2.2;
/** Zooming this far past a card level's limit, over a card, enters it (a group, then an area)... */
export const ENTER_OVERSCROLL = 1.35;
/** ...and zooming this far out past a level's limit steps out one level. */
export const LEAVE_OVERSCROLL = 0.7;

const shownSet = (visible) => visible || new Set(SYSTEM_MAP_NODES.map((n) => n.id));

/** The sections that show: those whose root is visible, in the data's order. */
export function overviewSections(visible) {
  const shown = shownSet(visible);
  return SYSTEM_MAP_SECTIONS.filter((s) => SYSTEM_MAP_NODES.some((n) => n.section === s.id && n.parent === null && shown.has(n.id)));
}

/** The groups that show: those with at least one visible area, in the data's order. */
export function overviewGroups(visible) {
  const sections = new Set(overviewSections(visible).map((s) => s.id));
  return SYSTEM_MAP_GROUPS.filter((g) => g.areas.some((a) => sections.has(a)));
}

/** How many parts of an area show. */
const partCount = (sectionId, shown) => SYSTEM_MAP_NODES.filter((n) => n.section === sectionId && shown.has(n.id)).length;

/**
 * Rows as even as they come: the fewest rows that keep every row within `max`
 * cards, then the columns that spread the cards over them (five cards, max
 * four: two rows of three and two; six: two rows of three).
 */
export function balancedColumns(count, max) {
  if (count <= 0) return 1;
  const rows = Math.ceil(count / max);
  return Math.ceil(count / rows);
}

/**
 * Cards on a grid, row by row in the given order; a short last row is centred
 * so the grid reads as one calm block. Two columns (a phone) use the compact
 * tile. Every card is {id, x, y, w, h, count}.
 */
export function cardGrid(items, {columns, compact = false, tileH = null, tileW = null} = {}) {
  const cols = Math.max(1, Math.min(columns, items.length || 1));
  const h = tileH || (compact ? AREA_H_COMPACT : AREA_H);
  const tw = tileW || AREA_W;
  const gap = compact ? AREA_GAP_COMPACT : AREA_GAP;
  const rows = Math.ceil(items.length / cols);
  const width = cols * tw + (cols - 1) * gap;
  const cards = items.map((it, i) => {
    const row = Math.floor(i / cols);
    const inRow = row === rows - 1 ? items.length - row * cols : cols;
    const offset = (width - (inRow * tw + (inRow - 1) * gap)) / 2;
    return {id: it.id, x: offset + (i % cols) * (tw + gap), y: row * (h + gap), w: tw, h, count: it.count};
  });
  return {
    cards,
    byId: new Map(cards.map((c) => [c.id, c])),
    columns: cols,
    compact,
    width,
    height: Math.max(0, rows * h + (rows - 1) * gap),
  };
}

/**
 * The all level: one card per group (those with a visible area), two by two on
 * a desktop, two across on a phone; a group's count is its visible areas.
 * @param {Object} opts
 * @param {Set<string>} opts.visible node ids that show (visibleSystemMapIds)
 * @param {number} opts.columns cards per row (ALL_COLUMNS, or 2 on a phone)
 * @param {boolean} opts.compact the phone's tiles
 */
export function layoutAll({visible, columns = ALL_COLUMNS, compact = false, tileH = null} = {}) {
  const shown = shownSet(visible);
  const sections = new Set(overviewSections(shown).map((s) => s.id));
  const groups = overviewGroups(shown).map((g) => ({id: g.id, count: g.areas.filter((a) => sections.has(a)).length}));
  return {...cardGrid(groups, {columns, compact, tileH}), relations: []};
}

/**
 * The height, in map units, a phone tile takes so `rows` of them fill the band
 * between the pill and the bar at the scale `s` the width gives (never under
 * the tile's natural height, never over AREA_H_COMPACT_MAX).
 */
export function phoneTileHeight({rows, availH, pad = 16, s, gap = AREA_GAP_COMPACT, min = AREA_H_COMPACT, max = AREA_H_COMPACT_MAX}) {
  if (!rows || !s) return min;
  const units = (availH - pad * 2) / s;
  return Math.round(Math.min(max, Math.max(min, (units - (rows - 1) * gap) / rows)));
}

/**
 * The group level: that group's visible areas as cards in balanced rows (at
 * most GROUP_MAX_COLUMNS across on a desktop, two on a phone), and the main
 * relationships between those areas as paths between card edges. A phone draws
 * no connectors: tiles two across read on their own, and connectors would cross
 * the tiles between. An area's count is its visible parts.
 * @param {string} groupId
 * @param {Object} opts
 * @param {Set<string>} opts.visible node ids that show (visibleSystemMapIds)
 * @param {number} opts.columns the most cards per row (GROUP_MAX_COLUMNS, or 2 on a phone)
 * @param {boolean} opts.compact the phone's tiles (rows are not balanced: two across, pan down)
 * @param {boolean} opts.balanced balanced rows (a desktop, or a phone on its side); by default not for compact tiles
 * @param {number} opts.tileH a tile height for the phone (phoneTileHeight), else the natural one
 * @param {number} opts.tileW a tile width (phoneGroupTile: one column as wide as two), else AREA_W
 */
export function layoutGroup(groupId, {visible, columns = GROUP_MAX_COLUMNS, compact = false, balanced = !compact, tileH = null, tileW = null} = {}) {
  const shown = shownSet(visible);
  const visibleSections = new Set(overviewSections(shown).map((s) => s.id));
  const sections = systemMapGroupSections(groupId).filter((s) => visibleSections.has(s.id));
  const cols = balanced ? balancedColumns(sections.length, columns) : columns;
  const grid = cardGrid(sections.map((s) => ({id: s.id, count: partCount(s.id, shown)})), {columns: cols, compact, tileH, tileW});
  const relations = compact ? [] : areaRelations()
      .filter((r) => grid.byId.has(r.a) && grid.byId.has(r.b))
      .map((r) => ({...r, d: areaPath(grid.byId.get(r.a), grid.byId.get(r.b))}));
  return {...grid, id: groupId, relations};
}

/**
 * How many tiles across an upright phone shows a group of `count` areas: two
 * across, except a group of one or two, which takes one column of tall tiles
 * that fill the band like the all level's (R56 L5: two small tiles in the
 * middle of an empty screen).
 */
export function phoneGroupColumns(count) {
  return count <= 2 ? 1 : OVERVIEW_COLUMNS_COMPACT;
}

/**
 * An upright phone's tiles for a group of `count` areas: how many across, how
 * wide (map units) and the most a tile may grow tall. One column of tiles is as
 * wide as two tiles and their gap, so the group fills the screen's width at the
 * same scale as a two-across group and its words come out the same size (R59
 * L3: the tiles of Playing and Showing it off read twice the size of the
 * others'); it is the tiles that grow, taller, to fill the band.
 * @returns {{columns: number, tileW: number, maxH: number}}
 */
export function phoneGroupTile(count) {
  const columns = phoneGroupColumns(count);
  if (columns !== 1) return {columns, tileW: AREA_W, maxH: AREA_H_COMPACT_MAX};
  const tileW = OVERVIEW_COLUMNS_COMPACT * AREA_W + (OVERVIEW_COLUMNS_COMPACT - 1) * AREA_GAP_COMPACT;
  return {columns, tileW, maxH: tileW};
}

/**
 * A phone on its side: a group of one or two areas takes the box a three-
 * across, two-row group takes (its tiles as wide and as tall as fill it), so it
 * fits at the same scale as the others and its words come out the same size.
 * Laid out at their natural size, its two tiles fitted about twice as large
 * (R61 N3: 31px names against 14.9px). Null for a bigger group.
 * @returns {{columns: number, tileW: number, tileH: number}|null}
 */
export function landscapeGroupTile(count) {
  if (!count || count > 2) return null;
  const cols = GROUP_MAX_COLUMNS_LANDSCAPE;
  const boxW = cols * AREA_W + (cols - 1) * AREA_GAP_COMPACT;
  const boxH = 2 * AREA_H_COMPACT + AREA_GAP_COMPACT;
  return {columns: count, tileW: Math.round((boxW - (count - 1) * AREA_GAP_COMPACT) / count), tileH: boxH};
}

/** Which card (a group's or an area's) a map-space point is over, or null. */
export function cardAt(grid, x, y) {
  for (const a of grid?.cards || []) {
    if (x >= a.x && x <= a.x + a.w && y >= a.y && y <= a.y + a.h) return a.id;
  }
  return null;
}

/**
 * How much a card level's type grows so a card's count pill (its smallest
 * type) reads at MIN_LINE_ON_SCREEN at the fitted scale: 1 when it already
 * does, up to OVERVIEW_TYPE_MAX_BOOST on a short canvas. The phone tile has its
 * own type and never needs it.
 */
export function overviewTypeScale(fitScale, {linePx = AREA_COUNT_PX, compact = false} = {}) {
  if (compact || !fitScale) return 1;
  return Math.min(OVERVIEW_TYPE_MAX_BOOST, Math.max(1, MIN_LINE_ON_SCREEN / (linePx * fitScale)));
}

/** The name grows with the count but more gently (its square root), so the hierarchy between them holds. */
export function overviewNameScale(typeScale) {
  return Math.sqrt(Math.max(1, typeScale || 1));
}

/**
 * Packs sibling blocks into shelves (rows) no wider than `avail`. Within a
 * shelf a block goes under the last column when it has room, so short blocks
 * stack beside tall ones instead of stretching the row; never under an
 * earlier column, so the reading order (columns left to right, stacked top to
 * bottom) is the siblings' order. A block wider than the whole width still
 * gets a column of its own.
 * @returns {Array<{w, h, columns: Array<{x, w, items: Array<{idx, y}>}>}>}
 */
export function packShelves(sizes, avail) {
  const shelves = [];
  let i = 0;
  while (i < sizes.length) {
    const columns = [];
    let width = 0, height = 0;
    for (; i < sizes.length; i++) {
      const s = sizes[i];
      const col = columns[columns.length - 1];
      if (col && col.used + ROW_GAP + s.h <= height) {
        const grown = Math.max(col.w, s.w);
        if (grown === col.w || width - col.w + grown <= avail) {
          col.items.push({idx: i, y: col.used + ROW_GAP});
          col.used += ROW_GAP + s.h;
          width += grown - col.w;
          col.w = grown;
          continue;
        }
      }
      const next = width + (columns.length ? COL_GAP : 0) + s.w;
      if (next > avail && columns.length) break;
      columns.push({x: width + (columns.length ? COL_GAP : 0), w: s.w, used: s.h, items: [{idx: i, y: 0}]});
      width = next;
      height = Math.max(height, s.h);
    }
    shelves.push({w: width, h: height, columns});
  }
  return shelves;
}

/**
 * One area's parts as a top-down tree from its root. A parent's children sit
 * under it, indented by the gutter, flowing into shelves no wider than
 * `maxWidth` (the screen's width at the readable scale) and stacking into
 * columns. Holds links are orthogonal rails: a trunk down from the parent in
 * its gutter, a bus above each shelf, a rail down each column and a stub into
 * each child's left edge, so no line ever crosses a card. Steps and `links`
 * (connections whose two ends both sit in this area) are routed around every
 * other card on a grid of lines beside the cards' edges (routeConnectors).
 * Connections to other areas are chips, not lines.
 */
export function layoutArea(sectionId, {visible, maxWidth = Infinity} = {}) {
  const shown = shownSet(visible);
  const inArea = SYSTEM_MAP_NODES.filter((n) => n.section === sectionId && shown.has(n.id));
  const childrenOf = new Map();
  for (const n of inArea) {
    if (!n.parent) continue;
    if (!childrenOf.has(n.parent)) childrenOf.set(n.parent, []);
    childrenOf.get(n.parent).push(n);
  }
  const kids = (id) => orderSiblings(childrenOf.get(id) || [], subtreeOf);
  // Every part under a node (itself included), for lifting steps to the siblings' level
  const subtreeOf = new Map();
  const collect = (id) => {
    const set = new Set([id]);
    for (const k of childrenOf.get(id) || []) for (const x of collect(k.id)) set.add(x);
    subtreeOf.set(id, set);
    return set;
  };
  for (const r of inArea) if (r.parent === null) collect(r.id);

  const measure = (n, avail) => {
    const ks = kids(n.id);
    if (!ks.length) return {w: NODE_W, h: NODE_H, shelves: [], sizes: []};
    const childAvail = Math.max(NODE_W, avail - GUTTER);
    const sizes = ks.map((k) => measure(k, childAvail));
    const shelves = packShelves(sizes, childAvail);
    let w = NODE_W, h = NODE_H;
    shelves.forEach((sh, i) => {
      w = Math.max(w, GUTTER + sh.w);
      h += (i === 0 ? HEAD_GAP : SHELF_GAP) + sh.h;
    });
    return {w, h, shelves, sizes};
  };

  const nodes = new Map();
  const holds = [];
  const railXs = new Set(), busYs = new Set();
  const place = (n, x, y, size, depth) => {
    nodes.set(n.id, {id: n.id, x, y, w: NODE_W, h: NODE_H, depth, section: sectionId, childCount: kids(n.id).length});
    const ks = kids(n.id);
    if (!ks.length) return;
    const trunkX = x + GUTTER / 2;
    railXs.add(trunkX);
    let shelfTop = y + NODE_H + HEAD_GAP;
    size.shelves.forEach((sh, si) => {
      const busY = shelfTop - (si === 0 ? HEAD_GAP : SHELF_GAP) / 2;
      busYs.add(busY);
      for (const col of sh.columns) {
        const colX = x + GUTTER + col.x;
        const railX = colX - GUTTER / 2;
        railXs.add(railX);
        for (const item of col.items) {
          const k = ks[item.idx];
          const ky = shelfTop + item.y;
          const cy = ky + NODE_H / 2;
          place(k, colX, ky, size.sizes[item.idx], depth + 1);
          holds.push({
            from: n.id,
            to: k.id,
            d: railPath(trunkX, y + NODE_H, busY, railX, cy, colX),
            lx: colX - 4,
            ly: cy - 7,
          });
        }
      }
      shelfTop += sh.h + SHELF_GAP;
    });
  };

  const roots = inArea.filter((n) => n.parent === null);
  let cursor = 0, width = 0;
  for (const r of roots) {
    const size = measure(r, maxWidth);
    place(r, 0, cursor, size, 0);
    cursor += size.h + SHELF_GAP;
    width = Math.max(width, size.w);
  }
  const height = roots.length ? cursor - SHELF_GAP : 0;
  const links = SYSTEM_MAP_LINKS.filter((l) => nodes.has(l.from) && nodes.has(l.to)).map((l) => ({...l}));
  const steps = SYSTEM_MAP_STEPS.filter((st) => nodes.has(st.from) && nodes.has(st.to)).map((st) => ({...st}));
  routeConnectors([...steps, ...links], nodes, {avoidX: [...railXs], avoidY: [...busYs]});
  return {id: sectionId, nodes, holds, links, steps, width, height, rootId: roots[0]?.id || null};
}

/**
 * Siblings in the order the steps ask for: a step from anything under sibling A
 * to anything under sibling B puts A before B. A stable topological sort, so
 * siblings no step orders keep the data's order.
 */
export function orderSiblings(siblings, subtreeOf) {
  if (siblings.length < 2) return siblings;
  const owner = new Map();
  for (const sib of siblings) for (const id of subtreeOf.get(sib.id) || [sib.id]) owner.set(id, sib.id);
  const before = new Map(siblings.map((sib) => [sib.id, new Set()]));
  for (const st of SYSTEM_MAP_STEPS) {
    const a = owner.get(st.from), b = owner.get(st.to);
    if (a && b && a !== b) before.get(b).add(a);
  }
  const out = [];
  const done = new Set();
  const pending = [...siblings];
  while (pending.length) {
    let i = pending.findIndex((sib) => [...before.get(sib.id)].every((a) => done.has(a)));
    if (i < 0) i = 0; // a cycle among siblings (the validator forbids it): keep the data's order
    const [sib] = pending.splice(i, 1);
    done.add(sib.id);
    out.push(sib);
  }
  return out;
}

// ── Connector routing ───────────────────────────────────────────────────────
// Steps and same-area links leave one card and enter another along horizontal
// and vertical runs on a grid of lines that sit ROUTE_MARGIN beside every
// card's edges, so a run never passes behind a card. The cheapest route is the
// shortest with the fewest bends that keeps off the holds rails. Connectors
// are routed one after another and no two share a port or a run: every
// connector on one side of a card gets its own port (PORT_SLOTS), and a grid
// edge another connector already runs along costs SHARED_RUN_COST times more,
// so a later connector takes a parallel lane or a different gap (R48 H1).
// Two connectors may still cross at a point.

/** The step leaves and enters a little below a card's middle, clear of the holds stub at its middle. */
const PORT_OFF = 18;
/** Vertical ports sit right of the middle, clear of the trunk in the left gutter. */
const PORT_X = 0.62;
/**
 * Where the connectors on one side of a card attach, preferred first: offsets
 * from the card's middle (left/right sides) or from its PORT_X point (top and
 * bottom). The left side keeps off the holds stub that enters at the middle.
 */
export const PORT_SLOTS = Object.freeze({
  right: Object.freeze([PORT_OFF, -PORT_OFF, 36, -36]),
  left: Object.freeze([PORT_OFF, 36, -PORT_OFF, -36]),
  vertical: Object.freeze([0, 32, -32, 64]),
});
/** A grid edge another connector already runs along costs this many times its length. */
export const SHARED_RUN_COST = 12;
/** Passing through another connector's port costs this much extra, so arrowheads stay clear. */
const PORT_NODE_COST = 60;
/** A vertical run left of the whole tree (the holds side) costs this much more per pixel. */
const LEFT_SIDE_COST = 1.3;

/** The sides a connector from `a` to `b` leaves and enters on. */
export function portSides(a, b) {
  if (b.x >= a.x + a.w) return {out: 'right', into: 'left'};
  if (b.x + b.w <= a.x) return {out: 'left', into: 'right'};
  if (b.y >= a.y + a.h) return {out: 'bottom', into: 'top'};
  return {out: 'top', into: 'bottom'};
}

const sideIsHorizontal = (side) => side === 'left' || side === 'right';

/** A point on a card's side: the offset is from the middle (left/right) or the PORT_X point (top/bottom). */
function portPoint(box, side, off) {
  if (side === 'right') return {x: box.x + box.w, y: box.y + box.h / 2 + off};
  if (side === 'left') return {x: box.x, y: box.y + box.h / 2 + off};
  if (side === 'bottom') return {x: box.x + box.w * PORT_X + off, y: box.y + box.h};
  return {x: box.x + box.w * PORT_X + off, y: box.y};
}

/** The point a margin outside a card's side, the first grid node a connector reaches. */
function outside(point, side, margin) {
  if (side === 'right') return {x: point.x + margin, y: point.y};
  if (side === 'left') return {x: point.x - margin, y: point.y};
  if (side === 'bottom') return {x: point.x, y: point.y + margin};
  return {x: point.x, y: point.y - margin};
}

/**
 * Where a connector leaves `a` and enters `b`: the facing sides, with the
 * point a margin outside each (the first grid node) alongside. `outOff` and
 * `inOff` are the port slots (PORT_SLOTS); by default the first slot of each
 * side (PORT_OFF below the middle, or the PORT_X point).
 */
export function connectorPorts(a, b, margin = ROUTE_MARGIN, {outOff = null, inOff = null} = {}) {
  const sides = portSides(a, b);
  const offA = outOff ?? (sideIsHorizontal(sides.out) ? PORT_OFF : 0);
  const offB = inOff ?? (sideIsHorizontal(sides.into) ? PORT_OFF : 0);
  const start = portPoint(a, sides.out, offA), end = portPoint(b, sides.into, offB);
  return {start, end, out: outside(start, sides.out, margin), into: outside(end, sides.into, margin), sides};
}

/**
 * Gives every connector its own port: the connectors on one side of a card
 * take that side's slots in the order of where their other end sits (the one
 * heading highest takes the highest port), so they fan out without crossing
 * each other at the card. More connectors than slots (never in the data) reuse
 * the last slot.
 * @returns {Array<{outOff: number, inOff: number}>} per connector
 */
export function assignPorts(connectors, nodes) {
  const groups = new Map();
  const add = (key, entry) => {
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(entry);
  };
  connectors.forEach((c, k) => {
    const a = nodes.get(c.from), b = nodes.get(c.to);
    const sides = portSides(a, b);
    add(`${c.from}:${sides.out}`, {k, end: 'out', other: sideIsHorizontal(sides.out) ? b.y + b.h / 2 : b.x + b.w / 2});
    add(`${c.to}:${sides.into}`, {k, end: 'in', other: sideIsHorizontal(sides.into) ? a.y + a.h / 2 : a.x + a.w / 2});
  });
  const offsets = connectors.map(() => ({outOff: null, inOff: null}));
  for (const [key, list] of groups) {
    const side = key.slice(key.lastIndexOf(':') + 1);
    const pool = side === 'left' ? PORT_SLOTS.left : side === 'right' ? PORT_SLOTS.right : PORT_SLOTS.vertical;
    const slots = pool.slice(0, Math.min(list.length, pool.length)).sort((x, y) => x - y);
    list.sort((x, y) => x.other - y.other || x.k - y.k).forEach((e, i) => {
      const off = slots[Math.min(i, slots.length - 1)];
      if (e.end === 'out') offsets[e.k].outOff = off;
      else offsets[e.k].inOff = off;
    });
  }
  return offsets;
}

/** True when the segment p→q (horizontal or vertical) passes through the inside of the box. */
export function segmentCrossesBox(p, q, box, tolerance = 0.5) {
  const x1 = Math.min(p.x, q.x), x2 = Math.max(p.x, q.x), y1 = Math.min(p.y, q.y), y2 = Math.max(p.y, q.y);
  const left = box.x + tolerance, right = box.x + box.w - tolerance, top = box.y + tolerance, bottom = box.y + box.h - tolerance;
  if (left >= right || top >= bottom) return false;
  return x1 < right && x2 > left && y1 < bottom && y2 > top;
}

/**
 * Routes every connector among `nodes` (a Map of boxes) around all the other
 * boxes. Each connector gets `points` (the polyline), `d` (with rounded
 * corners) and `mid` (a point along it for a label). One grid serves them all;
 * the connectors are routed shortest first, and each run a connector takes is
 * made expensive for the ones after it, so no two share a run or a port.
 */
export function routeConnectors(connectors, nodes, {margin = ROUTE_MARGIN, avoidX = [], avoidY = [], bend = 36} = {}) {
  if (!connectors.length) return connectors;
  const boxes = [...nodes.values()];
  const xs = new Set(), ys = new Set();
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const b of boxes) {
    xs.add(b.x - margin); xs.add(b.x + b.w + margin);
    ys.add(b.y - margin); ys.add(b.y + b.h + margin);
    minX = Math.min(minX, b.x); minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.w); maxY = Math.max(maxY, b.y + b.h);
  }
  // Room to go round the outside of everything, with a second lane
  xs.add(minX - margin * 2.5); xs.add(maxX + margin * 2.5);
  ys.add(minY - margin * 2.5); ys.add(maxY + margin * 2.5);
  xs.add(minX - margin * 4); xs.add(maxX + margin * 4);
  ys.add(minY - margin * 4); ys.add(maxY + margin * 4);
  const slots = assignPorts(connectors, nodes);
  const ports = connectors.map((c, k) => connectorPorts(nodes.get(c.from), nodes.get(c.to), margin, slots[k]));
  for (const p of ports) {
    xs.add(p.out.x); xs.add(p.into.x);
    ys.add(p.out.y); ys.add(p.into.y);
  }
  const X = [...xs].sort((a, b) => a - b), Y = [...ys].sort((a, b) => a - b);
  const xi = new Map(X.map((v, i) => [v, i])), yi = new Map(Y.map((v, i) => [v, i]));
  const inflated = boxes.map((b) => ({x: b.x - margin + 1, y: b.y - margin + 1, w: b.w + margin * 2 - 2, h: b.h + margin * 2 - 2}));
  const near = (v, list, tol = 6) => list.some((r) => Math.abs(r - v) <= tol);
  // Segment costs: horizontal between X[i] and X[i+1] on row j; vertical between Y[j] and Y[j+1] on column i
  const hCost = [], vCost = [], usedH = [], usedV = [];
  for (let j = 0; j < Y.length; j++) {
    hCost[j] = [];
    usedH[j] = [];
    const onBus = near(Y[j], avoidY);
    for (let i = 0; i < X.length - 1; i++) {
      const p = {x: X[i], y: Y[j]}, q = {x: X[i + 1], y: Y[j]};
      const blocked = inflated.some((b) => segmentCrossesBox(p, q, b, 0));
      hCost[j][i] = blocked ? Infinity : (X[i + 1] - X[i]) * (onBus ? 2.5 : 1);
    }
  }
  for (let i = 0; i < X.length; i++) {
    vCost[i] = [];
    usedV[i] = [];
    const onRail = near(X[i], avoidX);
    // The left of the tree is the holds side (trunks and rails run in the left gutters): a long
    // run down there reads as one more rail, so the right side is preferred when it is close (R48 L6)
    const leftOfTree = X[i] < minX;
    for (let j = 0; j < Y.length - 1; j++) {
      const p = {x: X[i], y: Y[j]}, q = {x: X[i], y: Y[j + 1]};
      const blocked = inflated.some((b) => segmentCrossesBox(p, q, b, 0));
      vCost[i][j] = blocked ? Infinity : (Y[j + 1] - Y[j]) * (onRail ? 2.5 : leftOfTree ? LEFT_SIDE_COST : 1);
    }
  }
  // Every connector's ports are grid nodes the others keep away from
  const portOwner = new Map();
  ports.forEach((p, k) => {
    portOwner.set(`${xi.get(p.out.x)},${yi.get(p.out.y)}`, k);
    portOwner.set(`${xi.get(p.into.x)},${yi.get(p.into.y)}`, k);
  });
  // Shortest first: they have the fewest alternatives
  const order = connectors.map((c, k) => k).sort((a, b) => {
    const la = Math.abs(ports[a].into.x - ports[a].out.x) + Math.abs(ports[a].into.y - ports[a].out.y);
    const lb = Math.abs(ports[b].into.x - ports[b].out.x) + Math.abs(ports[b].into.y - ports[b].out.y);
    return la - lb || a - b;
  });
  for (const k of order) {
    const c = connectors[k];
    const {start, end, out, into} = ports[k];
    const grid = dijkstraGrid(X, Y, xi, yi, hCost, vCost, out, into, bend, {usedH, usedV, portOwner, self: k});
    if (grid) {
      for (let n = 1; n < grid.length; n++) {
        const p = grid[n - 1], q = grid[n];
        if (p.y === q.y) usedH[yi.get(p.y)][Math.min(xi.get(p.x), xi.get(q.x))] = true;
        else usedV[xi.get(p.x)][Math.min(yi.get(p.y), yi.get(q.y))] = true;
      }
    }
    const points = simplifyPolyline([start, ...(grid || [out, into]), end]);
    c.points = points;
    c.d = roundedPath(points, Math.min(8, margin));
    c.mid = polylineMid(points);
  }
  return connectors;
}

/**
 * The cheapest grid path between two grid points: length plus a penalty per
 * bend, with a run another connector already took costing SHARED_RUN_COST
 * times more and another connector's port costing PORT_NODE_COST to pass.
 */
function dijkstraGrid(X, Y, xi, yi, hCost, vCost, from, to, bend, {usedH = null, usedV = null, portOwner = null, self = -1} = {}) {
  const si = xi.get(from.x), sj = yi.get(from.y), ti = xi.get(to.x), tj = yi.get(to.y);
  if (si === undefined || sj === undefined || ti === undefined || tj === undefined) return null;
  const W = X.length, H = Y.length;
  const DIRS = 2; // 0 horizontal, 1 vertical
  const key = (i, j, d) => (j * W + i) * DIRS + d;
  const dist = new Float64Array(W * H * DIRS).fill(Infinity);
  const prev = new Int32Array(W * H * DIRS).fill(-1);
  const heap = [];
  const push = (cost, i, j, d) => {
    heap.push({cost, i, j, d});
    let n = heap.length - 1;
    while (n > 0) {
      const p = (n - 1) >> 1;
      if (heap[p].cost <= heap[n].cost) break;
      [heap[p], heap[n]] = [heap[n], heap[p]];
      n = p;
    }
  };
  const pop = () => {
    const top = heap[0];
    const last = heap.pop();
    if (heap.length) {
      heap[0] = last;
      let n = 0;
      for (;;) {
        const l = n * 2 + 1, r = l + 1;
        let m = n;
        if (l < heap.length && heap[l].cost < heap[m].cost) m = l;
        if (r < heap.length && heap[r].cost < heap[m].cost) m = r;
        if (m === n) break;
        [heap[m], heap[n]] = [heap[n], heap[m]];
        n = m;
      }
    }
    return top;
  };
  const nodeCost = (i, j) => {
    if (!portOwner || (i === ti && j === tj)) return 0;
    const owner = portOwner.get(`${i},${j}`);
    return owner !== undefined && owner !== self ? PORT_NODE_COST : 0;
  };
  for (let d = 0; d < DIRS; d++) { dist[key(si, sj, d)] = 0; push(0, si, sj, d); }
  let found = -1;
  while (heap.length) {
    const {cost, i, j, d} = pop();
    const k = key(i, j, d);
    if (cost > dist[k]) continue;
    if (i === ti && j === tj) { found = k; break; }
    const tryMove = (ni, nj, nd, edge, shared) => {
      if (!Number.isFinite(edge)) return;
      const nc = cost + edge * (shared ? SHARED_RUN_COST : 1) + (nd !== d ? bend : 0) + nodeCost(ni, nj);
      const nk = key(ni, nj, nd);
      if (nc < dist[nk]) { dist[nk] = nc; prev[nk] = k; push(nc, ni, nj, nd); }
    };
    if (i + 1 < W) tryMove(i + 1, j, 0, hCost[j][i], usedH?.[j]?.[i]);
    if (i > 0) tryMove(i - 1, j, 0, hCost[j][i - 1], usedH?.[j]?.[i - 1]);
    if (j + 1 < H) tryMove(i, j + 1, 1, vCost[i][j], usedV?.[i]?.[j]);
    if (j > 0) tryMove(i, j - 1, 1, vCost[i][j - 1], usedV?.[i]?.[j - 1]);
  }
  if (found < 0) return null;
  const path = [];
  for (let k = found; k >= 0; k = prev[k]) {
    const cell = Math.floor(k / DIRS);
    path.push({x: X[cell % W], y: Y[Math.floor(cell / W)]});
  }
  return path.reverse();
}

/**
 * Where two connectors run along the same line (a horizontal run on one within
 * `tolerance` of a horizontal run on the other, overlapping in x; likewise
 * vertical), with the shared length. A crossing at a point is not an overlap.
 * @returns {Array<{a: string, b: string, length: number}>}
 */
export function connectorOverlaps(connectors, {tolerance = 3} = {}) {
  const out = [];
  const segs = (c) => {
    const list = [];
    for (let i = 1; i < (c.points || []).length; i++) list.push([c.points[i - 1], c.points[i]]);
    return list;
  };
  for (let i = 0; i < connectors.length; i++) {
    for (let j = i + 1; j < connectors.length; j++) {
      let length = 0;
      for (const [p, q] of segs(connectors[i])) {
        for (const [r, s] of segs(connectors[j])) {
          const ph = Math.abs(p.y - q.y) < 1e-6, rh = Math.abs(r.y - s.y) < 1e-6;
          if (ph && rh && Math.abs(p.y - r.y) <= tolerance) {
            length += Math.max(0, Math.min(Math.max(p.x, q.x), Math.max(r.x, s.x)) - Math.max(Math.min(p.x, q.x), Math.min(r.x, s.x)));
          } else if (!ph && !rh && Math.abs(p.x - r.x) <= tolerance) {
            length += Math.max(0, Math.min(Math.max(p.y, q.y), Math.max(r.y, s.y)) - Math.max(Math.min(p.y, q.y), Math.min(r.y, s.y)));
          }
        }
      }
      if (length > 1) out.push({a: `${connectors[i].from}>${connectors[i].to}`, b: `${connectors[j].from}>${connectors[j].to}`, length});
    }
  }
  return out;
}

/** Pairs of connectors whose ends (where they leave or enter a card) sit within `tolerance` of each other. */
export function sharedPorts(connectors, {tolerance = 8} = {}) {
  const out = [];
  const ends = (c) => [c.points[0], c.points[c.points.length - 1]];
  for (let i = 0; i < connectors.length; i++) {
    for (let j = i + 1; j < connectors.length; j++) {
      for (const p of ends(connectors[i])) {
        for (const q of ends(connectors[j])) {
          if (Math.hypot(p.x - q.x, p.y - q.y) < tolerance) out.push({a: `${connectors[i].from}>${connectors[i].to}`, b: `${connectors[j].from}>${connectors[j].to}`});
        }
      }
    }
  }
  return out;
}

/** Drops repeated and collinear points from a polyline. */
export function simplifyPolyline(points) {
  const out = [];
  for (const p of points) {
    const last = out[out.length - 1];
    if (last && Math.abs(last.x - p.x) < 1e-6 && Math.abs(last.y - p.y) < 1e-6) continue;
    out.push({x: p.x, y: p.y});
  }
  for (let i = 1; i < out.length - 1;) {
    const a = out[i - 1], b = out[i], c = out[i + 1];
    const collinear = (Math.abs(a.x - b.x) < 1e-6 && Math.abs(b.x - c.x) < 1e-6) || (Math.abs(a.y - b.y) < 1e-6 && Math.abs(b.y - c.y) < 1e-6);
    if (collinear) out.splice(i, 1);
    else i++;
  }
  return out;
}

/** An SVG path along a polyline with its corners rounded. */
export function roundedPath(points, r = 8) {
  if (!points.length) return '';
  if (points.length === 1) return `M${points[0].x} ${points[0].y}`;
  let d = `M${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length - 1; i++) {
    const a = points[i - 1], b = points[i], c = points[i + 1];
    const inLen = Math.hypot(b.x - a.x, b.y - a.y), outLen = Math.hypot(c.x - b.x, c.y - b.y);
    const rr = Math.min(r, inLen / 2, outLen / 2);
    const p1 = {x: b.x - (b.x - a.x) / inLen * rr, y: b.y - (b.y - a.y) / inLen * rr};
    const p2 = {x: b.x + (c.x - b.x) / outLen * rr, y: b.y + (c.y - b.y) / outLen * rr};
    d += ` L${p1.x} ${p1.y} Q${b.x} ${b.y}, ${p2.x} ${p2.y}`;
  }
  const last = points[points.length - 1];
  return `${d} L${last.x} ${last.y}`;
}

/** The point halfway along a polyline. */
export function polylineMid(points) {
  if (points.length < 2) return points[0] || {x: 0, y: 0};
  let total = 0;
  for (let i = 1; i < points.length; i++) total += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  let left = total / 2;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1], b = points[i];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (left <= len) {
      const t = len ? left / len : 0;
      return {x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t};
    }
    left -= len;
  }
  return points[points.length - 1];
}

/**
 * The rail from a parent to one child: down the parent's trunk, along the bus
 * above the child's shelf, down the child's column rail and into its left
 * edge, with rounded corners. Siblings share the trunk and bus, so their
 * paths overlap there.
 */
export function railPath(trunkX, fromY, busY, railX, cy, toX) {
  const r = 8;
  if (railX === trunkX) return `M${trunkX} ${fromY} V${cy - r} Q${trunkX} ${cy}, ${trunkX + r} ${cy} H${toX}`;
  return `M${trunkX} ${fromY} V${busY - r} Q${trunkX} ${busY}, ${trunkX + r} ${busY} H${railX - r} Q${railX} ${busY}, ${railX} ${busY + r} V${cy - r} Q${railX} ${cy}, ${railX + r} ${cy} H${toX}`;
}

/**
 * A link between any two boxes: leaves and enters on the nearer sides, bowing so
 * it reads as a different kind of line from the holds links. Used for the soft
 * connectors between area cards on the overview.
 */
export function linkPath(a, b, {bowMin = 80} = {}) {
  const ac = {x: a.x + a.w / 2, y: a.y + a.h / 2};
  const bc = {x: b.x + b.w / 2, y: b.y + b.h / 2};
  const dx = bc.x - ac.x, dy = bc.y - ac.y;
  let x1, y1, x2, y2, c1x, c1y, c2x, c2y;
  if (Math.abs(dx) >= Math.abs(dy)) {
    const dir = dx >= 0 ? 1 : -1;
    x1 = ac.x + dir * a.w / 2; y1 = ac.y;
    x2 = bc.x - dir * b.w / 2; y2 = bc.y;
    const bow = Math.max(bowMin, Math.abs(dx) * 0.35);
    c1x = x1 + dir * bow; c1y = y1;
    c2x = x2 - dir * bow; c2y = y2;
  } else {
    const dir = dy >= 0 ? 1 : -1;
    x1 = ac.x; y1 = ac.y + dir * a.h / 2;
    x2 = bc.x; y2 = bc.y - dir * b.h / 2;
    const bow = Math.max(bowMin * 0.75, Math.abs(dy) * 0.35);
    c1x = x1; c1y = y1 + dir * bow;
    c2x = x2; c2y = y2 - dir * bow;
  }
  const mid = bezierPoint([x1, y1], [c1x, c1y], [c2x, c2y], [x2, y2], 0.5);
  return {d: `M${x1} ${y1} C${c1x} ${c1y}, ${c2x} ${c2y}, ${x2} ${y2}`, mid};
}

/** A soft connector between two area cards on the overview. */
export function areaPath(a, b) {
  return linkPath(a, b, {bowMin: 40}).d;
}

export function bezierPoint(p0, p1, p2, p3, t) {
  const u = 1 - t;
  return {
    x: u * u * u * p0[0] + 3 * u * u * t * p1[0] + 3 * u * t * t * p2[0] + t * t * t * p3[0],
    y: u * u * u * p0[1] + 3 * u * u * t * p1[1] + 3 * u * t * t * p2[1] + t * t * t * p3[1],
  };
}

/** True when two laid-out boxes overlap (used by the test and the tray). */
export function boxesOverlap(a, b) {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/** The gap between two boxes (0 when they touch or overlap). */
export function boxDistance(a, b) {
  const dx = Math.max(0, b.x - (a.x + a.w), a.x - (b.x + b.w));
  const dy = Math.max(0, b.y - (a.y + a.h), a.y - (b.y + b.h));
  return Math.hypot(dx, dy);
}

/** The transform that fits a box into a viewport with padding. */
export function fitTransform(box, viewport, pad = 32, {minScale = 0.02, maxScale = 1.6} = {}) {
  const vw = Math.max(1, viewport.w - pad * 2);
  const vh = Math.max(1, viewport.h - pad * 2);
  const s = Math.min(maxScale, Math.max(minScale, Math.min(vw / box.w, vh / box.h)));
  return {
    s,
    x: viewport.w / 2 - (box.x + box.w / 2) * s,
    y: viewport.h / 2 - (box.y + box.h / 2) * s,
  };
}

/** The part of the canvas not covered by the bars laid over it. */
export function availableViewport(viewport, inset = {top: 0, bottom: 0}) {
  const top = inset?.top || 0, bottom = inset?.bottom || 0;
  return {x: 0, y: top, w: viewport.w, h: Math.max(1, viewport.h - top - bottom)};
}

/**
 * Where the camera starts on the overview: the whole map on a desktop; on a
 * phone the tiles fill the width and you pan down. Returns the view plus the
 * scales the zoom range is built from.
 */
export function overviewEntryView(overview, viewport, {pad = 28, inset, fillWidth = false, maxScale = 1} = {}) {
  const avail = availableViewport(viewport, inset);
  const box = {x: 0, y: 0, w: Math.max(1, overview.width), h: Math.max(1, overview.height)};
  // maxScale caps a small grid (a two-card group) at the size the other card levels show, so every card level reads alike (R53 L5)
  const all = fitTransform(box, {w: avail.w, h: avail.h}, pad, {maxScale: Math.min(1, maxScale || 1)});
  const fitScale = all.s;
  if (!fillWidth) return {x: all.x, y: all.y + avail.y, s: fitScale, fitScale, startScale: fitScale};
  const s = Math.min(1, Math.max(fitScale, (avail.w - pad * 2) / box.w));
  return {x: (avail.w - box.w * s) / 2, y: avail.y + pad, s, fitScale, startScale: s};
}

/**
 * The width an area's tree may take, in its own units, so that it fits the
 * screen's width at the readable scale (a phone: at full size).
 */
export function areaLayoutWidth(viewport, {pad = 48, readable = READABLE_SCALE} = {}) {
  return Math.max(NODE_W + GUTTER * 3, Math.floor((viewport.w - pad * 2) / readable));
}

/**
 * Where the camera starts inside an area: the whole tree when it reads at a
 * comfortable size (down to `floor`, where a line is still MIN_LINE_ON_SCREEN),
 * otherwise the root at the top and the rest to pan down to, at the scale
 * nearest `readable` (between `floor` and `ceiling`, never wider than the
 * screen) that puts the canvas's bottom edge in a gap between cards or past
 * the last one, so the first screen never cuts a card (R48 M2). The tree was
 * laid out to the screen's width, so what does not fit is below.
 */
export function areaEntryView(area, viewport, {pad = 40, inset, readable = READABLE_SCALE, floor = readable, hardFloor = floor, ceiling = ENTRY_CEILING_SCALE, maxScale = 1.3} = {}) {
  const avail = availableViewport(viewport, inset);
  const box = {x: 0, y: 0, w: Math.max(1, area.width), h: Math.max(1, area.height)};
  const fitScale = fitTransform(box, {w: avail.w, h: avail.h}, pad, {maxScale: 99}).s;
  const low = Math.min(floor, readable);
  // Whole, at the usual padding, or at a tighter one when that is what it takes to stay at a readable size
  for (const p of [pad, Math.min(pad, ENTRY_TIGHT_PAD)]) {
    const fit = fitTransform(box, {w: avail.w, h: avail.h}, p, {maxScale});
    if (fit.s >= low - 1e-9) return {x: fit.x, y: fit.y + avail.y, s: fit.s, fitScale, startScale: fit.s, whole: true};
  }
  // Nearest the readable size first; only when no scale in [floor, ceiling] lands the edge in a gap may it go a
  // little smaller, down to `hardFloor` (R56 M1: at 1440×900 the measured two-row bar left the canvas 4.3 rows tall,
  // between the 11px floor and the screen's width, so it fell back to a cut row)
  const gapOpts = {availH: avail.h, availW: avail.w, pad, readable, ceiling: Math.max(readable, ceiling)};
  const {s, top} = entryInGap(area, {...gapOpts, floor: low}) || entryInGap(area, {...gapOpts, floor: Math.min(low, hardFloor)}) || {s: readable, top: ENTRY_TIGHT_PAD};
  const view = {x: box.w * s <= avail.w - pad * 2 ? (avail.w - box.w * s) / 2 : pad, y: avail.y + top, s};
  return {...clampTranslate(view, box, viewport, {inset, pad}), fitScale, startScale: s, whole: false};
}

/** The layout widths tried, as a share of the screen's readable width, when an area's first screen would cut a card (R56 M1). */
export const AREA_WIDTH_TRIALS = Object.freeze([1, 0.94, 0.88, 0.82]);

/** True when an entry view's canvas bottom edge (above the bottom inset) passes through a card. */
export function viewCutsCard(area, view, viewport, inset = {top: 0, bottom: 0}) {
  const bottom = viewport.h - (inset?.bottom || 0);
  for (const n of area.nodes.values()) {
    const y1 = n.y * view.s + view.y, y2 = (n.y + n.h) * view.s + view.y;
    if (y1 + 1 < bottom && y2 - 1 > bottom) return true;
  }
  return false;
}

/**
 * The area's layout and entry view together: the tree laid out to the screen's
 * readable width first; when its first screen would cut a card at the readable
 * floor, a slightly narrower layout (which leaves the scale room to grow up to
 * the ceiling without passing the screen's width) is tried, and only then the
 * hard floor, widest layout first. The rows sit 134 units apart, so a canvas
 * whose height falls between two rows' worth (1440×900 on /help, under the
 * two-row bar) had no gap to land in at a scale of 0.88–0.91 (R56 M1).
 * `layout` is the caller's (memoised) layoutArea.
 * @returns {{area: object, view: object, maxWidth: number}}
 */
export function areaEntryLayout(sectionId, {visible, maxWidth, viewport, entry = {}, layout = layoutArea} = {}) {
  let first = null;
  const floor = entry.floor ?? entry.readable ?? READABLE_SCALE;
  for (const hard of [floor, entry.hardFloor ?? floor]) {
    for (const f of AREA_WIDTH_TRIALS) {
      const width = Math.max(NODE_W + GUTTER * 3, Math.floor(maxWidth * f));
      const area = layout(sectionId, {visible, maxWidth: width});
      const view = areaEntryView(area, viewport, {...entry, hardFloor: hard});
      const out = {area, view, maxWidth: width};
      if (!first) first = out;
      if (!area.nodes.size || view.whole || !viewCutsCard(area, view, viewport, entry.inset)) return out;
      if (f !== 1 && area.width >= first.area.width) break; // narrower asked for, nothing changed: no point in more
    }
  }
  return first;
}

/** The tightest padding an entry view may use, above and below, to fit an area whole or land its bottom edge in a gap; an entry that does not fit whole starts this far under the bar, 8px under it with the inset (R56 M1: no empty band). */
export const ENTRY_TIGHT_PAD = 4;
/**
 * The smallest an entry view may make the parts when nothing from the 11px
 * floor up lands the bottom edge in a gap: a name at 12.5px, a line at 10.4px.
 * With it every desktop canvas 540px tall or more ends in a gap; the usual
 * sizes (1440, 1280, 1920 and the embeds) never need it.
 */
export const ENTRY_HARD_FLOOR_SCALE = (MIN_NAME_ON_SCREEN - 0.5) / PART_NAME_PX;
/** How far under the bar (plus its inset) an entry that does not fit whole may start before it reads as an empty band: further only when nothing nearer ends in a gap. */
export const ENTRY_TOP_MAX = 12;
/** The bottom edge lands at least this far inside a gap between rows of cards. */
const GAP_INSET = 4;

/** True when the canvas's bottom edge, at this scale with the root `top` from the canvas's top, passes through a card. */
export function entryCutsCard(area, s, {availH, top}) {
  const bottom = (availH - top) / s; // the map y at the canvas's bottom edge
  for (const n of area.nodes.values()) {
    if (n.y + 1 < bottom && n.y + n.h - 1 > bottom) return true;
  }
  return false;
}

/** Half the band a horizontal connector run occupies: the canvas's bottom edge keeps this far from one (R51 M1). */
export const LANE_HALF = 8;

/** The y of every horizontal run of the area's routed connectors (steps and same-area links), in map units. */
export function laneYs(area) {
  const ys = new Set();
  for (const c of [...(area.steps || []), ...(area.links || [])]) {
    const pts = c.points || [];
    for (let i = 1; i < pts.length; i++) if (Math.abs(pts[i].y - pts[i - 1].y) < 1e-6) ys.add(pts[i].y);
  }
  return [...ys].sort((a, b) => a - b);
}

/** The y ranges, in map units, where no card lies: between the rows and past the last one. */
export function rowGaps(area) {
  const spans = [...area.nodes.values()].map((n) => [n.y, n.y + n.h]).sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (const [y1, y2] of spans) {
    const last = merged[merged.length - 1];
    if (last && y1 <= last[1]) last[1] = Math.max(last[1], y2);
    else merged.push([y1, y2]);
  }
  const gaps = [];
  for (let i = 1; i < merged.length; i++) gaps.push([merged[i - 1][1], merged[i][0]]);
  if (merged.length) gaps.push([merged[merged.length - 1][1], Infinity]);
  return gaps;
}

/**
 * The scale and top offset of an entry view that cuts no card: for every gap
 * between rows and every top offset from `pad` down to ENTRY_TIGHT_PAD, the
 * scales that put the canvas's bottom edge inside that gap, kept within
 * [floor, ceiling] and never wider than the screen; the one nearest
 * `readable` wins, the tightest top preferred (the first row sits just under
 * the bar, no empty band: R56 M1). Null when no gap can be reached.
 * @returns {{s: number, top: number} | null}
 */
export function entryInGap(area, {availH, availW, pad, readable, floor, ceiling}) {
  // Never wider than the screen; a tree already wider than it at the readable scale (a phone's deep branch) is panned sideways anyway
  const widest = (availW - pad * 2) / Math.max(1, area.width);
  const high = widest >= readable ? Math.min(ceiling, widest) : ceiling;
  const tops = [];
  for (let t = Math.min(pad, ENTRY_TIGHT_PAD); t <= pad + 1e-9; t += 4) tops.push(t);
  const lanes = laneYs(area);
  let best = null;
  for (const top of tops) {
    const reach = availH - top;
    for (const [g1, g2] of rowGaps(area)) {
      const lo = Math.max(floor, Number.isFinite(g2) ? reach / (g2 - GAP_INSET) : floor);
      const hi = Math.min(high, reach / (g1 + GAP_INSET));
      if (lo > hi + 1e-9) continue;
      const s = clearOfLanes(Math.min(hi, Math.max(lo, readable)), {reach, lanes, lo, hi, readable});
      // Nearest the readable size, the first row nearest the bar; past ENTRY_TOP_MAX the gap under the bar reads as an empty band (R56 M1), so only when nothing nearer ends in a gap
      const score = Math.abs(s - readable) + (top - Math.min(pad, ENTRY_TIGHT_PAD)) * 0.0005 + Math.max(0, top - ENTRY_TOP_MAX) * 0.01 + (onLane(reach / s, lanes) ? 0.05 : 0);
      if (!best || score < best.score - 1e-12) best = {s, top, score};
    }
  }
  return best ? {s: best.s, top: best.top} : null;
}

const onLane = (y, lanes) => lanes.some((l) => Math.abs(l - y) < LANE_HALF);

/**
 * A scale within [lo, hi] whose bottom edge (reach / s, in map units) runs along
 * no connector lane: the given one when it is clear, else the nearest to
 * `readable` just above or below the lane it hits (R51 M1: a step along the
 * canvas's edge read as two loose stubs).
 */
function clearOfLanes(s, {reach, lanes, lo, hi, readable}) {
  if (!onLane(reach / s, lanes)) return s;
  const lane = lanes.find((l) => Math.abs(l - reach / s) < LANE_HALF);
  const options = [reach / (lane + LANE_HALF), reach / (lane - LANE_HALF)].filter((x) => x >= lo - 1e-9 && x <= hi + 1e-9 && !onLane(reach / x, lanes));
  if (!options.length) return s;
  return options.sort((a, b) => Math.abs(a - readable) - Math.abs(b - readable))[0];
}

/**
 * How far you may zoom at a level. The card levels (all, group): a little
 * smaller than the fit, up to a few times it (further, over a card, enters it).
 * Area: a little smaller than the whole area, up to a part card at about twice
 * its size.
 */
export function scaleRange(level, {fitScale, startScale = fitScale}) {
  if (level === LEVELS.AREA) return {min: Math.min(fitScale * 0.85, startScale), max: Math.max(PART_MAX_SCALE, startScale)};
  return {min: fitScale * 0.9, max: Math.max(startScale * 2.6, fitScale)};
}

export function clampScale(s, range) {
  return Math.min(range.max, Math.max(range.min, s));
}

/**
 * Keeps the content on screen: a box narrower than the viewport is centred,
 * a wider one may not be panned past its own edges (plus a little padding).
 */
export function clampTranslate(view, box, viewport, {inset = {top: 0, bottom: 0}, pad = 24} = {}) {
  const avail = availableViewport(viewport, inset);
  const s = view.s;
  const cw = box.w * s, ch = box.h * s;
  let x = view.x, y = view.y;
  if (cw <= avail.w - pad * 2) x = avail.x + (avail.w - cw) / 2 - box.x * s;
  else x = Math.min(avail.x + pad - box.x * s, Math.max(avail.x + avail.w - pad - (box.x + box.w) * s, x));
  if (ch <= avail.h - pad * 2) y = avail.y + (avail.h - ch) / 2 - box.y * s;
  else y = Math.min(avail.y + pad - box.y * s, Math.max(avail.y + avail.h - pad - (box.y + box.h) * s, y));
  return {x, y, s};
}

/**
 * How many cards a view cuts at the edges of the canvas left clear of the bars
 * (a card partly under the bar, partly past the bottom or a side edge). Cards
 * wholly off screen do not count.
 */
export function viewEdgeCuts(area, view, viewport, inset = {top: 0, bottom: 0}) {
  const avail = availableViewport(viewport, inset);
  const top = avail.y, bottom = avail.y + avail.h, right = avail.w;
  let cuts = 0;
  for (const n of area.nodes.values()) {
    const r = toScreenBox(n, view);
    if (r.y + r.h <= top + 1 || r.y >= bottom - 1 || r.x + r.w <= 1 || r.x >= right - 1) continue;
    if (r.y < top - 1 || r.y + r.h > bottom + 1 || r.x < -1 || r.x + r.w > right + 1) cuts++;
  }
  return cuts;
}

/**
 * The view a picked part (a search result, a chip, a deep link to a part) is
 * shown at: the area's entry scale, with the rows and columns lined up so the
 * canvas's edges fall in the gaps between cards, the part whole on screen and
 * as near the middle as that allows. Rows are tried flush under the bar and
 * flush above the bottom edge, columns flush with either side; the view that
 * cuts the fewest neighbours wins, then the one that puts the part nearest
 * the middle. A tree that fits whole is shown whole (its entry view), and so
 * is any entry that already shows the part whole with no card cut. Centring
 * the part instead cut its neighbours at the top, the bottom and the sides
 * (R59 L4). Screen pixels.
 */
export function partFocusView(area, id, viewport, {inset = {top: 0, bottom: 0}, entry, pad = 24} = {}) {
  const pos = area?.nodes.get(id);
  if (!pos || !entry) return null;
  if (entry.whole) return {x: entry.x, y: entry.y, s: entry.s};
  const s = entry.s;
  const avail = availableViewport(viewport, inset);
  // The entry already shows the part whole with nothing cut: the pick stays there (it only selects and pulses). The
  // search below clamps to the pan bounds, which can move a first screen that ends in a gap so a row runs under the
  // bar (R61 N2: Two-step in People at /help 1920 cut Players)
  const atEntry = toScreenBox(pos, entry);
  if (atEntry.y >= avail.y - 0.5 && atEntry.y + atEntry.h <= avail.y + avail.h + 0.5 && atEntry.x >= -0.5 && atEntry.x + atEntry.w <= avail.w + 0.5
    && viewEdgeCuts(area, entry, viewport, inset) === 0) return {x: entry.x, y: entry.y, s: entry.s};
  const box = {x: 0, y: 0, w: Math.max(1, area.width), h: Math.max(1, area.height)};
  const nodes = [...area.nodes.values()];
  const tight = ENTRY_TIGHT_PAD;
  const ys = new Set([entry.y]);
  for (const n of nodes) {
    ys.add(avail.y + tight - n.y * s);
    ys.add(avail.y + avail.h - tight - (n.y + n.h) * s);
  }
  const xs = new Set([entry.x]);
  if (box.w * s > avail.w - pad * 2) {
    for (const n of nodes) {
      xs.add(tight - n.x * s);
      xs.add(avail.w - tight - (n.x + n.w) * s);
    }
  }
  const mid = {x: avail.w / 2, y: avail.y + avail.h / 2};
  let best = null;
  for (const y0 of ys) {
    for (const x0 of xs) {
      // Within the pan bounds (a narrow tree stays centred), allowing the tight pad the entry itself uses
      const v = clampTranslate({x: x0, y: y0, s}, box, viewport, {inset, pad: tight});
      const r = toScreenBox(pos, v);
      if (r.y < avail.y - 0.5 || r.y + r.h > avail.y + avail.h + 0.5 || r.x < -0.5 || r.x + r.w > avail.w + 0.5) continue;
      const cuts = viewEdgeCuts(area, v, viewport, inset);
      const off = Math.hypot(r.x + r.w / 2 - mid.x, r.y + r.h / 2 - mid.y);
      const score = cuts * 1000 + off;
      if (!best || score < best.score - 1e-9) best = {...v, score};
    }
  }
  if (!best) {
    // Taller than the canvas or no aligned spot: centre it, as before
    return clampTranslate({s, x: avail.w / 2 - (pos.x + pos.w / 2) * s, y: mid.y - (pos.y + pos.h / 2) * s}, box, viewport, {inset, pad: tight});
  }
  return {x: best.x, y: best.y, s: best.s};
}

/** True when two camera views are the same place (the camera has not been moved since it was put there). */
export function sameView(a, b, {eps = 0.5, epsScale = 1e-3} = {}) {
  return !!a && !!b && Math.abs(a.s - b.s) < epsScale && Math.abs(a.x - b.x) < eps && Math.abs(a.y - b.y) < eps;
}

/**
 * Zooming past a level's limit: `over` is how far past the clamp the zoom was
 * asked to go (the product of the refused factors; 1 = not at all).
 */
export function overscrollAction(level, over) {
  if (level !== LEVELS.AREA && over >= ENTER_OVERSCROLL) return 'enter';
  if (level !== LEVELS.ALL && over <= LEAVE_OVERSCROLL) return 'leave';
  return null;
}

/** After a zoom steps a level, the burst (wheel notches, pinch, held key, button clicks) must go quiet for this long before another step. */
export const STEP_QUIET_MS = 320;
/** Zoom inputs further apart than this do not add up towards an overscroll. */
export const OVERSCROLL_RESET_MS = 700;

/**
 * The state machine behind zooming across levels (R53 H1). Zoom inputs that
 * the clamp refuses add up (`over`); once they pass the level's overscroll
 * limit the stepper fires one 'enter' or 'leave' and LATCHES: every further
 * input, until the burst has been quiet for `quietMs`, only extends the latch
 * and fires nothing, so a fast wheel, a trackpad pinch-out or a run of clicks
 * steps exactly one level per burst and can never run past the all level or
 * off the page. A discrete press (the zoom-out button, the - key) with the
 * scale already at the level's floor steps out at once, so slow clicks step
 * out as surely as fast ones. A pinch reports its overscroll when it ends
 * (`offer`). Pure: the clock is injected for the test.
 */
export function createZoomStepper({quietMs = STEP_QUIET_MS, resetMs = OVERSCROLL_RESET_MS, now = () => Date.now()} = {}) {
  let level = LEVELS.ALL;
  let over = 1;
  let lastAt = -Infinity;
  let latchedUntil = -Infinity;
  const isLatched = (t) => t < latchedUntil;
  const fire = (t, action) => {
    over = 1;
    latchedUntil = t + quietMs;
    return action;
  };
  const extend = (t) => {
    latchedUntil = t + quietMs;
    over = 1;
    lastAt = t;
    return null;
  };
  return {
    /** The level the camera is at now (a level change resets the accumulation, never the latch). */
    setLevel(next) {
      level = next;
      over = 1;
    },
    /**
     * One zoom input. `requested` is the scale asked for, `clamped` what the
     * range allowed, `factor` the input's factor, `discrete` a button or key
     * press, `atMin` / `atMax` whether the scale was already at the range's
     * floor / ceiling before the input. Returns 'enter', 'leave' or null.
     */
    zoom({requested, clamped, factor, discrete = false, atMin = false, atMax = false}) {
      const t = now();
      if (isLatched(t)) return extend(t);
      if (Math.abs(requested - clamped) < 1e-6) {
        over = 1;
        lastAt = t;
        return null;
      }
      if (discrete && factor < 1 && atMin && level !== LEVELS.ALL) return fire(t, 'leave');
      if (discrete && factor > 1 && atMax && level !== LEVELS.AREA) return fire(t, 'enter');
      if (t - lastAt > resetMs) over = 1;
      lastAt = t;
      over *= factor;
      const action = overscrollAction(level, over);
      return action ? fire(t, action) : null;
    },
    /** A finished pinch that pushed past the limit: its action, once, unless the latch is on (then the latch is extended). */
    offer(action) {
      const t = now();
      if (!action) return null;
      if (isLatched(t)) return extend(t);
      return fire(t, action);
    },
    get latched() { return isLatched(now()); },
    get over() { return over; },
  };
}

/**
 * One level change at a time: the page begins a change (a fly-in, a step out,
 * a Back it asked the browser for) and ends it when the change has landed;
 * meanwhile every other request to change level is refused. A change that
 * never reports back frees the gate after `timeoutMs`.
 */
export function createLevelGate({now = () => Date.now(), timeoutMs = 1500} = {}) {
  let busy = false;
  let since = 0;
  let queued = null;
  const stillBusy = () => busy && now() - since < timeoutMs;
  return {
    begin() {
      if (stillBusy()) return false;
      busy = true;
      since = now();
      return true;
    },
    end() {
      busy = false;
      if (!queued) return;
      const run = queued;
      queued = null;
      run();
    },
    /**
     * A press that should not be lost (the Back arrow, Esc, Backspace, the
     * phone pill): runs now when the gate is free, else is kept, at most one,
     * and runs once the change in flight has landed (R56 L1: a deliberate
     * double-tap goes out two levels, one after the other, never skipping one;
     * a third tap while one is kept is dropped). False when dropped.
     */
    later(run) {
      if (!stillBusy()) { queued = null; run(); return true; }
      if (queued) return false;
      queued = run;
      return true;
    },
    get busy() { return stillBusy(); },
    get queued() { return !!queued; },
  };
}

/**
 * What can be hovered and clicked at a level. Decided by the level, never by
 * what is under the cursor: at the all level the whole group card is the
 * target, inside a group the whole area card, and no part can be reached until
 * you are inside its area.
 */
export function interactivity(level) {
  if (level === LEVELS.AREA) return {groupCards: false, areaCards: false, parts: true, hoverParts: true, chips: true};
  if (level === LEVELS.GROUP) return {groupCards: false, areaCards: true, parts: false, hoverParts: false, chips: false};
  return {groupCards: true, areaCards: false, parts: false, hoverParts: false, chips: false};
}

/** The screen rectangle of a map box under a view. */
export function toScreenBox(box, view) {
  return {x: box.x * view.s + view.x, y: box.y * view.s + view.y, w: box.w * view.s, h: box.h * view.s};
}

/** Which side of the anchor a box sits on. */
export function sideOf(box, anchor) {
  if (box.x >= anchor.x + anchor.w) return 'right';
  if (box.x + box.w <= anchor.x) return 'left';
  if (box.y >= anchor.y + anchor.h) return 'below';
  return 'above';
}

// ── The chips tray ──────────────────────────────────────────────────────────
export const CHIP_W = 232;
export const CHIP_H = 34;
export const CHIP_GAP_X = 8;
export const CHIP_GAP_Y = 6;
/** The caption above the chips naming the card they belong to. */
export const TRAY_CAP_H = 20;

/**
 * The shapes a tray of `items` chips may take, preferred first: one column,
 * then wider, shorter grids (2–4 columns) for when a column has no room, as
 * beside a root with its whole tree underneath. Each carries the caption row.
 */
export function trayShapes(items) {
  if (!items) return [];
  return [1, 2, 3, 4].filter((c) => c === 1 || c < items).map((c) => {
    const rows = Math.ceil(items / c);
    return {cols: c, w: c * CHIP_W + (c - 1) * CHIP_GAP_X, h: TRAY_CAP_H + rows * CHIP_H + (rows - 1) * CHIP_GAP_Y};
  });
}

/** A tray further than this from its card is "far": tethered by its caption and (through clear canvas) a leader. */
export const TRAY_FAR = 28;
/** A far tray never sits nearer than this to any other card, where its chips would read as that card's (R48 M1). */
export const TRAY_CROWD = 24;
/** How far from its card a tray may be placed. */
export const TRAY_REACH = 480;

/** The chips' boxes inside a placed tray (screen space), mirroring the tray's CSS grid: the caption row, then rows of chips. */
export function trayChipBoxes(tray, count) {
  const cols = Math.max(1, tray.cols || 1);
  const out = [];
  for (let i = 0; i < count; i++) {
    const col = i % cols, row = Math.floor(i / cols);
    out.push({x: tray.x + col * (CHIP_W + CHIP_GAP_X), y: tray.y + TRAY_CAP_H + row * (CHIP_H + CHIP_GAP_Y), w: CHIP_W, h: CHIP_H});
  }
  return out;
}

/**
 * Where the chips tray goes: the nearest free spot beside the card it hangs
 * off, inside the canvas, over no other card (nor the bars). Beside the card
 * is preferred, then under it, then above. A spot whose corridor (the convex
 * hull of the card and the tray, where a straight pointer move travels) passes
 * over no other card costs less; a far spot (TRAY_FAR) that hugs some other
 * card (within TRAY_CROWD) is never used, since its chips would read as that
 * card's. The tray may take any of the given shapes (one column of chips
 * first; wider, shorter grids when a column has no room). When no shape has a
 * free spot within reach the tray is not shown (the dialog has the same
 * chips). Screen pixels.
 * @param {Object} o
 * @param {{x,y,w,h}} o.anchor the card on screen
 * @param {Array<{w,h,cols}>|{w,h}} o.shapes the tray's possible sizes, preferred first
 * @param {Array<{x,y,w,h}>} o.obstacles the other cards and the bars, on screen
 * @param {{x,y,w,h}} o.bounds where the tray may be (the canvas less a margin)
 */
export function placeTray({anchor, shapes, size, obstacles = [], bounds, gap = 10, step = 16, reach = TRAY_REACH, bias = {right: 0, left: 24, below: 14, above: 40}, crowd = TRAY_CROWD, fallback = true}) {
  const list = Array.isArray(shapes) ? shapes : (shapes || size ? [shapes || size] : []);
  if (!anchor || !list.length || !bounds) return null;
  const inBounds = (r) => r.x >= bounds.x && r.y >= bounds.y && r.x + r.w <= bounds.x + bounds.w && r.y + r.h <= bounds.y + bounds.h;
  const pad = 6;
  const blocked = obstacles.map((o) => ({x: o.x - pad, y: o.y - pad, w: o.w + pad * 2, h: o.h + pad * 2}));
  const acy = anchor.y + anchor.h / 2;
  // A far spot that hugs another card is refused while any other spot exists; when it is the only one (the camera has
  // been panned so the clear spots are off the canvas) it is used, marked crowded, rather than no tray at all (R51 M2)
  const strict = fallback === 'only' ? null : searchTray(crowd);
  if (strict || !fallback) return strict;
  const crowded = searchTray(0);
  return crowded ? {...crowded, crowded: true} : null;

  function searchTray(crowdLimit) {
    let best = null;
    list.forEach((shape, si) => {
      const xs = new Set(), ys = new Set();
      const addAround = (set, lo, hi, extent) => {
        for (let k = 0; k * step <= reach; k++) {
          set.add(hi + gap + k * step);                    // past the far edge, then further out
          set.add(lo - gap - extent - k * step);           // before the near edge, then further out
          set.add(lo + k * step);                          // aligned with the card, sliding along it
          set.add(hi - extent - k * step);
          set.add(lo - k * step);
        }
      };
      addAround(xs, anchor.x, anchor.x + anchor.w, shape.w);
      addAround(ys, anchor.y, anchor.y + anchor.h, shape.h);
      // Flush against the canvas's edges too (under the bar, at the right edge): the room a wide tray needs beside a root
      // is often exactly what is left there (R51 M2: a candidate grid stepping 16px from the card missed it by 3px)
      xs.add(bounds.x); xs.add(bounds.x + bounds.w - shape.w);
      ys.add(bounds.y); ys.add(bounds.y + bounds.h - shape.h);
      // ...and flush against the cards near enough to matter: after a pan the free room (beside a card further along,
      // under a row) seldom lines up with a 16px grid from this card (R56 M2: Scoring systems after 60px, Join link)
      for (const o of blocked) {
        if (boxDistance(o, anchor) > reach) continue;
        xs.add(o.x + o.w); xs.add(o.x - shape.w);
        ys.add(o.y + o.h); ys.add(o.y - shape.h);
      }
      for (const y of ys) {
        for (const x of xs) {
          const r = {x, y, w: shape.w, h: shape.h};
          if (!inBounds(r)) continue;
          const d = boxDistance(r, anchor);
          if (d < gap - 0.01 || d > reach) continue;
          const side = sideOf(r, anchor);
          const align = side === 'right' || side === 'left' ? Math.abs(r.y + r.h / 2 - acy) : Math.abs(r.x - anchor.x);
          const base = d * 1.4 + align * 0.5 + bias[side] + si * 40;
          // The cheap part of the score first: a spot that cannot beat the best so far needs no obstacle checks (the
          // tray is re-placed on every frame of a pan, so this has to stay quick: R56 M2)
          if (best && base >= best.score) continue;
          if (blocked.some((o) => boxesOverlap(r, o))) continue;
          const far = d > TRAY_FAR;
          if (far && crowdLimit && obstacles.some((o) => boxDistance(r, o) < crowdLimit)) continue;
          // A corridor that passes over other cards means the pointer must cross them on the way: last resort
          const crosses = far && obstacles.some((o) => polygonHitsBox(trayCorridor(anchor, r), o));
          const score = base + (crosses ? 90 : 0);
          if (!best || score < best.score) best = {x, y, w: shape.w, h: shape.h, cols: shape.cols || 1, side, distance: d, score};
        }
      }
    });
    return best;
  }
}

const boxCorners = (r) => [{x: r.x, y: r.y}, {x: r.x + r.w, y: r.y}, {x: r.x + r.w, y: r.y + r.h}, {x: r.x, y: r.y + r.h}];

/** The convex hull of some points (Andrew's monotone chain), counter-clockwise, no repeated last point. */
/**
 * Where a desktop tray may go: the canvas less 8px at the sides and the
 * bottom, and from the inset's own edge at the top (the inset is the bar plus
 * 4px). A two-row tray between the bar and an area's first row then keeps its
 * 24px from that row at every bar height: with 4px more it missed it by
 * 0.02px at rest on /help 1440, and Scoring systems dropped to three chips
 * where a 20px pan gave five (R61 N1).
 */
export function trayBounds(viewport, inset = {top: 0, bottom: 0}) {
  return {x: 8, y: inset.top, w: viewport.w - 16, h: viewport.h - inset.top - 8};
}

/** The most chips a desktop tray shows; past it a "+N more" chip opens the part's dialog. */
export const TRAY_MAX = 6;

/**
 * The chip counts a tray may show, most first: all of them (up to `max`, the
 * rest behind "+N more"; one over `max` shows them all, since "+1 more" would
 * take the same room), then one fewer at a time down to one, each with "+N
 * more" for the rest. After a pan the room left beside a card is often too
 * small for six chips but fine for five or four, so the tray steps down one
 * chip at a time instead of dropping straight to three (R56 M2, R59 L2).
 * @returns {Array<{shown: number, more: number}>}
 */
export function trayOptions(total, max = TRAY_MAX) {
  const out = [];
  const top = total === max + 1 ? total : Math.min(max, total);
  for (let shown = top; shown >= 1; shown--) {
    if (shown < top && shown === total - 1 && top === total) continue; // never "+1 more" when all of them fit in the same room
    out.push({shown, more: total - shown});
  }
  return out;
}

/**
 * The tray for a card with `total` connections, placed in the current screen
 * geometry: the fullest tray that has a clear spot (one that hugs no other
 * card when away from its own), then, only when none has, the fullest whose
 * only spot is crowded. Null when no tray fits at all (the dialog has the same
 * chips). The result carries `shown` and `more`.
 */
export function placeTrayFor({anchor, total, obstacles = [], bounds, max = TRAY_MAX}) {
  const options = trayOptions(total, max);
  // Only the cards near enough to touch a tray within reach can block one
  const near = (o) => o.x < anchor.x + anchor.w + TRAY_REACH + 1000 && o.x + o.w > anchor.x - TRAY_REACH - 1000 && o.y < anchor.y + anchor.h + TRAY_REACH + 400 && o.y + o.h > anchor.y - TRAY_REACH - 400;
  const close = obstacles.filter(near);
  for (const fallback of [false, 'only']) {
    for (const o of options) {
      const shapes = trayShapes(o.shown + (o.more ? 1 : 0));
      const t = placeTray({anchor, shapes, obstacles: close, bounds, fallback});
      if (t) return {...t, shown: o.shown, more: o.more};
    }
  }
  return null;
}

export function convexHull(points) {
  const pts = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
  if (pts.length < 3) return pts;
  const cross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

/**
 * The corridor the pointer may travel in from a card to its tray without the
 * hover switching to a card it crosses: the convex hull of the two, which holds
 * every straight line from any point of the card to any chip, however the
 * tray sits (beside, above, touching the card's edge or offset from it).
 * @returns {Array<{x,y}>} a convex polygon, screen space
 */
export function trayCorridor(anchor, tray) {
  return convexHull([...boxCorners(anchor), ...boxCorners(tray)]);
}

const distToSegment = (p, a, b) => {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2)) : 0;
  return Math.hypot(p.x - (a.x + dx * t), p.y - (a.y + dy * t));
};

/** True when the point lies in the corridor (a convex polygon), grown by a little slack. */
export function pointInCorridor(p, corridor, slack = 8) {
  const n = corridor.length;
  if (n < 3) return corridor.some((q) => Math.hypot(q.x - p.x, q.y - p.y) <= slack);
  let inside = true;
  for (let i = 0; i < n; i++) {
    const a = corridor[i], b = corridor[(i + 1) % n];
    if ((b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x) < 0) { inside = false; break; }
  }
  if (inside) return true;
  for (let i = 0; i < n; i++) if (distToSegment(p, corridor[i], corridor[(i + 1) % n]) <= slack) return true;
  return false;
}

/** True when a convex polygon and a box overlap (separating axis test over both shapes' edges). */
export function polygonHitsBox(poly, box) {
  const b = boxCorners(box);
  const axes = [{x: 1, y: 0}, {x: 0, y: 1}];
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i], q = poly[(i + 1) % poly.length];
    axes.push({x: -(q.y - p.y), y: q.x - p.x});
  }
  for (const ax of axes) {
    let min1 = Infinity, max1 = -Infinity, min2 = Infinity, max2 = -Infinity;
    for (const p of poly) { const v = p.x * ax.x + p.y * ax.y; min1 = Math.min(min1, v); max1 = Math.max(max1, v); }
    for (const p of b) { const v = p.x * ax.x + p.y * ax.y; min2 = Math.min(min2, v); max2 = Math.max(max2, v); }
    if (max1 <= min2 + 1e-9 || max2 <= min1 + 1e-9) return false;
  }
  return true;
}

/** True when the segment p→q (any direction) passes through the inside of the box. */
export function segmentHitsBox(p, q, box) {
  const inside = (r) => r.x > box.x && r.x < box.x + box.w && r.y > box.y && r.y < box.y + box.h;
  if (inside(p) || inside(q)) return true;
  // Liang–Barsky clip against the box
  const dx = q.x - p.x, dy = q.y - p.y;
  let t0 = 0, t1 = 1;
  const checks = [[-dx, p.x - box.x], [dx, box.x + box.w - p.x], [-dy, p.y - box.y], [dy, box.y + box.h - p.y]];
  for (const [den, num] of checks) {
    if (Math.abs(den) < 1e-9) { if (num < 0) return false; continue; }
    const t = num / den;
    if (den < 0) { if (t > t1) return false; if (t > t0) t0 = t; }
    else { if (t < t0) return false; if (t < t1) t1 = t; }
  }
  return t1 - t0 > 1e-6;
}

/** The leader line from a card to a tray placed away from it (screen space). */
export function leaderLine(anchor, tray) {
  const side = sideOf(tray, anchor);
  const clampY = (y) => Math.min(Math.max(y, tray.y + 17), tray.y + tray.h - 17);
  const clampX = (x) => Math.min(Math.max(x, tray.x + 20), tray.x + tray.w - 20);
  const acx = anchor.x + anchor.w / 2, acy = anchor.y + anchor.h / 2;
  if (side === 'right') return {x1: anchor.x + anchor.w, y1: acy, x2: tray.x, y2: clampY(acy)};
  if (side === 'left') return {x1: anchor.x, y1: acy, x2: tray.x + tray.w, y2: clampY(acy)};
  if (side === 'below') return {x1: clampX(acx), y1: anchor.y + anchor.h, x2: clampX(acx), y2: tray.y};
  return {x1: clampX(acx), y1: anchor.y, x2: clampX(acx), y2: tray.y + tray.h};
}

/** The leader is drawn only through clear canvas: never over another card or its text (R48 M1). */
export function leaderIsClear(leader, obstacles) {
  if (!leader) return false;
  const p = {x: leader.x1, y: leader.y1}, q = {x: leader.x2, y: leader.y2};
  return !obstacles.some((o) => segmentHitsBox(p, q, o));
}

// ── The URL ↔ the page's state ──────────────────────────────────────────
// #area or #part (selected) and ?node= (open part). Entering from the overview
// is one history entry (Back leaves the area, a second Back leaves the page);
// opening a part's dialog is another (Back closes the dialog); switching
// areas, selecting and leaving replace.

/**
 * What a route asks the map to show: {group, area, select, open}, or null for
 * the all level. `#group` is the group level; `#area` and `#part` are inside
 * the area (its group comes with it); `?node=` opens the part.
 */
export function routeWants({hash = '', node = null} = {}, visible = null) {
  const shown = (id) => !visible || visible.has(id);
  const hashId = (hash || '').replace(/^#/, '');
  const inArea = (areaId, select, open) => ({group: systemMapGroupOf(areaId)?.id || null, area: areaId, select, open});
  if (node && findSystemMapNode(node) && shown(node)) return inArea(findSystemMapNode(node).section, node, true);
  if (hashId && findSystemMapGroup(hashId)) return {group: hashId, area: null, select: null, open: false};
  if (hashId && findSystemMapSection(hashId)) return inArea(hashId, null, false);
  if (hashId && findSystemMapNode(hashId) && shown(hashId)) return inArea(findSystemMapNode(hashId).section, hashId, false);
  return null;
}

/** The hash and node param for a state: {hash, node}. The deepest thing shown names the hash. */
export function routeFor({groupId = null, areaId = null, selectedId = null, openId = null} = {}) {
  let hash = '';
  if (areaId) hash = `#${selectedId && selectedId !== areaId ? selectedId : areaId}`;
  else if (groupId) hash = `#${groupId}`;
  return {hash, node: openId || null};
}

/** The level a state is at. */
const levelOf = (state) => (state.areaId ? 2 : state.groupId ? 1 : 0) + (state.areaId && state.openId ? 1 : 0);

/**
 * Whether a move between two states is a new history entry (going deeper: into
 * a group, into an area, opening a part's dialog), a replacement (anything
 * else that changes the URL: switching, selecting, closing, stepping out) or
 * nothing (the URL is the same). Back then steps out one level at a time.
 */
export function historyAction(from, to) {
  const a = routeFor(from), b = routeFor(to);
  if (a.hash === b.hash && (a.node || null) === (b.node || null)) return 'none';
  const same = (from.areaId || null) === (to.areaId || null) && (from.groupId || null) === (to.groupId || null);
  if (levelOf(to) > levelOf(from) && (same || !from.areaId)) return 'push';
  return 'replace';
}

/**
 * The states to pass through on the way from one to another when the move
 * skips levels (a search result or the Areas menu from the all level goes
 * straight into an area): one history entry per level, so Back steps out one
 * level at a time. A move that skips nothing is just [to].
 */
export function historySteps(from, to) {
  if (historyAction(from, to) !== 'push') return [to];
  const steps = [];
  if (!from.groupId && to.groupId && to.areaId) steps.push({groupId: to.groupId, areaId: null, selectedId: null, openId: null});
  if (!from.areaId && to.areaId && to.openId) steps.push({...to, openId: null});
  steps.push(to);
  return steps;
}

/**
 * The element id the router's scroll-to-hash should find: a group, area or
 * part id, and any other plain id-like hash too (`#nope`), so an in-session
 * hash change to an unknown one finds its anchor and the router does not warn
 * (R56 L2; the map shows All for it). Anything that is not a plain id: null.
 */
export function hashAnchorId(hash) {
  let id = (hash || '').replace(/^#/, '');
  try { id = decodeURIComponent(id); } catch (err) { return null; }
  if (!id) return null;
  if (findSystemMapGroup(id) || findSystemMapSection(id) || findSystemMapNode(id)) return id;
  return /^[A-Za-z][\w-]{0,63}$/.test(id) ? id : null;
}

// ── The pre-paint script ────────────────────────────────────────────────────

/**
 * What a shared link may name, for the pre-paint script: every group's and
 * every area's tint, and every part's area. The script opens a link straight at
 * its target only when the id is one of these (as routeWants does).
 */
export function prefitTargets() {
  const noAdmin = visibleSystemMapIds({admin: false});
  const shownAreas = new Set(overviewSections(noAdmin).map((x) => x.id));
  const shownGroups = new Set(overviewGroups(noAdmin).map((g) => g.id));
  const allShown = new Set(overviewSections(visibleSystemMapIds({admin: true})).map((x) => x.id));
  return {
    g: Object.fromEntries(SYSTEM_MAP_GROUPS.map((g) => [g.id, sectionTone(g).tint])),
    a: Object.fromEntries(SYSTEM_MAP_SECTIONS.map((x) => [x.id, sectionTone(x).tint])),
    n: Object.fromEntries(SYSTEM_MAP_NODES.map((n) => [n.id, n.section])),
    // What the bar says at the target before the page takes over (R61 N4): a group's name; an area's group, name and line
    gn: Object.fromEntries(SYSTEM_MAP_GROUPS.map((g) => [g.id, g.name])),
    an: Object.fromEntries(SYSTEM_MAP_SECTIONS.map((x) => [x.id, [systemMapGroupOf(x.id)?.id || '', x.name, x.line || '']])),
    // The current name's ink (a group's or an area's, as the page colours it) and a group's area count, box ticked and
    // unticked, so the words sit and read exactly as the page's own crumbs will (R62 P1)
    gi: Object.fromEntries(SYSTEM_MAP_GROUPS.map((g) => [g.id, sectionTone(g).ink])),
    ai: Object.fromEntries(SYSTEM_MAP_SECTIONS.map((x) => [x.id, sectionTone(x).ink])),
    gc: Object.fromEntries(SYSTEM_MAP_GROUPS.map((g) => [g.id, [allShown, shownAreas].map((set) => systemMapGroupSections(g.id).filter((x) => set.has(x.id)).length)])),
    // Hidden with "Show admin features" unticked: such a link falls back to All, so the bar names nothing for it
    h: Object.fromEntries([
      ...SYSTEM_MAP_NODES.filter((n) => !noAdmin.has(n.id)).map((n) => n.id),
      ...SYSTEM_MAP_SECTIONS.filter((x) => !shownAreas.has(x.id)).map((x) => x.id),
      ...SYSTEM_MAP_GROUPS.filter((g) => !shownGroups.has(g.id)).map((g) => g.id),
    ].map((id) => [id, 1])),
  };
}

/** Where the map remembers its one checkbox (per browser, a convenience): read by the page and by the pre-paint script. */
export const SYSTEM_MAP_STORE_KEY = 'tl.systemMap.v2';

/**
 * The bar's words at a link's target before the page takes over (R61 N4), or null for the all level. An area: its
 * group's name (the middle crumb), its own name and line; a group: its name and its area count (box ticked or not).
 * `ink` is the current name's colour. The pre-paint script writes exactly these (R62 P1).
 */
export function prefitCrumbs(id, targets = prefitTargets(), {adminOff = false} = {}) {
  const T = targets;
  const aid = T.a[id] ? id : T.n[id];
  if (aid) {
    const A = T.an[aid];
    return {group: T.gn[A[0]] || '', current: A[1], line: A[2], count: '', ink: T.ai[aid]};
  }
  if (T.g[id]) {
    const n = (T.gc[id] || [0, 0])[adminOff ? 1 : 0];
    return {group: '', current: T.gn[id], line: '', count: `${n} ${n === 1 ? 'area' : 'areas'}`, ink: T.gi[id]};
  }
  return null;
}

/**
 * The map's pre-paint script (inline in the server's markup right after the
 * map's bar, R59 L1). It defines window.__smFit, which fits the all level to
 * the real canvas under the bar's real height (mirroring overviewEntryView +
 * fitTransform with maxScale 1, and overviewTypeScale / overviewNameScale) and
 * marks the canvas data-fitted (until then the all level is not drawn). It
 * reads the link (?node= first, then the #hash, as routeWants does): when it
 * names a group, an area or a part, the map's root takes data-sm-deep and its
 * canvas and bar the target's tint (on them, not on the root, whose style the
 * page binds: hydration would see a mismatch), so the all level is never drawn and the canvas already wears
 * the target's colour when the map opens there on mount (R59 M1). It runs the
 * fit itself unless a page sizes the map's box first (data-sm-sizer on an
 * ancestor: /help), in which case the page's sizer calls __smFit. On a link
 * to a target it also writes the target's crumbs (prefitCrumbs) into the
 * bar's custom properties and marks the root data-sm-crumbs, so the bar never
 * reads "All · 15 areas · 168 parts" over a blank tinted canvas; with the box
 * remembered unticked it marks data-sm-admin-off, which hides the all-level
 * count and the box's tick until the page has applied it (R61 N4). The words
 * go into a copy of the page's own crumb row (the Back arrow, the separators,
 * the icon's room), so nothing moves when the page's row replaces it (R62 P1).
 * Its first act is to mark the root data-sm-pre: until then (or until the page
 * is ready) the bar's count is not drawn, so a frame painted between the bar
 * and this script never shows the all-level count over a deep link (R62 P2).
 */
export function prefitScript() {
  return `(function(){var T=${JSON.stringify(prefitTargets())};window.__smFit=function(){var cs=document.querySelectorAll(".sm-canvas[data-fit-w]");for(var n=0;n<cs.length;n++){var c=cs[n];if(c.getAttribute("data-mounted"))continue;var d=c.dataset,W=+d.fitW,H=+d.fitH,pad=+d.fitPad,bar=c.parentNode&&c.parentNode.querySelector(".sm-bar"),bh=bar?Math.round(bar.getBoundingClientRect().height):0,top=bh?bh+4:+d.fitTop;var r=c.getBoundingClientRect();if(!r.width||!r.height)continue;var ah=r.height-top;var s=Math.min(1,Math.max(0.02,Math.min(Math.max(1,r.width-pad*2)/W,Math.max(1,ah-pad*2)/H)));var x=r.width/2-W/2*s,y=top+ah/2-H/2*s;c.style.setProperty("--sm-tf","translate3d("+x+"px, "+y+"px, 0) scale("+s+")");var k=Math.min(1.6,Math.max(1,11/(19*s)));c.style.setProperty("--area-type-k",k);c.style.setProperty("--area-name-k",Math.sqrt(k));c.setAttribute("data-fitted","1");}};var me=document.currentScript,sm=me&&me.closest?me.closest(".sm"):null;if(!sm)return;sm.setAttribute("data-sm-pre","");try{var h=decodeURIComponent((location.hash||"").slice(1)),q=new URLSearchParams(location.search).get("node"),id=q&&T.n[q]?q:h,tint=T.g[id]||T.a[id]||(T.n[id]&&T.a[T.n[id]]);var off=false;try{var sv=JSON.parse(localStorage.getItem(${JSON.stringify(SYSTEM_MAP_STORE_KEY)})||"null");off=!!sv&&sv.admin===false;}catch(e){}if(off)sm.setAttribute("data-sm-admin-off","");if(tint){sm.setAttribute("data-sm-deep","");var ts=sm.querySelectorAll(".sm-canvas,.sm-bar");for(var i=0;i<ts.length;i++)ts[i].style.setProperty("--sm-deep-tint",tint);var b=sm.querySelector(".sm-bar");if(b&&!(off&&T.h[id])){var aid=T.a[id]?id:T.n[id],gr="",cu,li="",ct="",ink;if(aid){var A=T.an[aid];gr=T.gn[A[0]]||"";cu=A[1];li=A[2];ink=T.ai[aid];sm.setAttribute("data-sm-crumb-area","");}else{var gc=T.gc[id]||[0,0],nn=gc[off?1:0];cu=T.gn[id];ct=nn+(nn===1?" area":" areas");ink=T.gi[id];}var P={"--sm-group":gr,"--sm-current":cu||"","--sm-count":ct};for(var k in P)b.style.setProperty(k,JSON.stringify(P[k]));if(ink)b.style.setProperty("--sm-ink",ink);sm.setAttribute("data-sm-crumbs","");if(li){b.style.setProperty("--sm-line",JSON.stringify(li));sm.setAttribute("data-sm-crumb-line","");}}}}catch(e){}if(!sm.closest("[data-sm-sizer]"))window.__smFit();})();`;
}
